import RecipientAvatar from 'components/RecipientAvatar'
import { useRecipientsResolution } from 'hooks/useRecipientResolution'
import { equals, isEmpty, map } from 'ramda'
import React, { CSSProperties, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { validateEmail } from 'utils/email'

import './style.css'

interface SenderInfoProps {
  senderId?: number,
  senderIds?: number[]
  messageText?: string,
  textStyles?: CSSProperties
}

const SenderInfo = ({ senderIds, messageText = null, textStyles = {} }: SenderInfoProps) => {
  const { t } = useTranslation()

  const organizationName = useSelector(state => (
    state.embed.configuration.organizationName
  ))

  let recipients = useRecipientsResolution(senderIds)

  const senderText = useMemo(() => {
    if (messageText) {
      return messageText
    }

    // we only show the name iff there is one senderId
    if (senderIds.length > 1 || isEmpty(recipients)) {
      return ''
    }

    const recipient = recipients[0]?.recipient

    if (!recipient || !recipient.name) {
      return ''
    }

    // If the organization name is an email address we don't want to show it for PII reasons
    const hasInvalidOrganizationName = !organizationName || !organizationName.length || validateEmail(organizationName)

    // If the sender name matches the org name just provide the name instead of "Drift from Drift"
    const senderNameMatchesOrgName = !!organizationName && recipient.name.toLowerCase() === organizationName.toLowerCase()

    if (hasInvalidOrganizationName || senderNameMatchesOrgName) {
      return `${recipient.name}`
    }

    return t('header.senderInfo', {
      name: recipient.name,
      orgName: organizationName
    })
  }, [senderIds.length, recipients, organizationName, messageText, t])

  return (
    <div className="drift-widget-greeting-agent-header">
      <span className="drift-widget-greeting-recipient-name" style={textStyles}>
        {senderText}
      </span>
      {map(recipient => {
        const id = recipient?.recipient?.id
        return <RecipientAvatar key={id} id={id} />
      }, recipients)}
    </div>
  )
}

const propsAreEqual = (prevProp: SenderInfoProps, nextProp: SenderInfoProps) => {
  return (prevProp.senderId === nextProp.senderId && prevProp.messageText === nextProp.messageText) ||
    (equals(prevProp.senderIds, nextProp.senderIds) && prevProp.messageText === nextProp.messageText)
}

export default React.memo(SenderInfo, propsAreEqual)
